import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { type SongRow, titleOf, artistOf, isReady, requestSong } from "../lib/analysis";
import { type CatalogTrack, searchCatalog } from "../lib/catalog";

const minutes = (seconds: number | null) =>
  seconds ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}` : null;


/** The cover, where we have one. The tinted square stays as the fallback so a
 *  row never collapses while an image loads or when a song has no artwork. */
function Art({ src, alt }: { src?: string | null; alt: string }) {
  if (!src) return <span className="art" />;
  return <img className="art" src={src} alt={alt} loading="lazy" />;
}

export function Home() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SongRow[]>([]);
  const [catalog, setCatalog] = useState<CatalogTrack[]>([]);
  const [recent, setRecent] = useState<SongRow[]>([]);
  const [signedIn, setSignedIn] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.from("songs").select("*").eq("status", "done").is("duplicate_of", null)
      .not("lyrics_mode", "is", null).neq("lyrics_mode", "pending")
      .order("updated_at", { ascending: false }).limit(18)
      .then(({ data }) => setRecent((data ?? []) as SongRow[]));

    supabase.auth.getSession().then(({ data }) => setSignedIn(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange(
      (_event, session) => setSignedIn(!!session));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) { setResults([]); setCatalog([]); return; }
    const timer = setTimeout(() => {
      // The library and the catalogue are asked in parallel: one says what we
      // already have chords for, the other what exists at all.
      supabase.rpc("search_songs", { q: text })
        .then(({ data }) => setResults((data ?? []) as SongRow[]));
      searchCatalog(text).then(setCatalog);
    }, 250);                                  // one query per pause, not per keystroke
    return () => clearTimeout(timer);
  }, [query]);

  async function add(track: CatalogTrack) {
    setError(null);
    setBusy(track.catalogId);
    try {
      const song = await requestSong(track);
      if (song) navigate(`/song/${song.slug}`);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Could not add that song.");
    } finally {
      setBusy(null);
    }
  }

  const searching = query.trim().length >= 2;
  const shown = searching ? results : recent;
  // Anything already in the library should not also appear as "add me".
  const have = new Set(results.map((song) => song.slug));
  const missing = catalog.filter((track) => !have.has(track.slug));

  return (
    <div className="page">
      <h1 className="serif hero">everychord</h1>
      <p className="muted">Chords and words for any song, worked out from the recording.</p>

      <input
        className="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search a song or an artist"
        autoFocus
      />

      {error && <p className="small" style={{ color: "var(--accent)" }}>{error}</p>}

      <h2 className="section-heading serif">
        {searching ? "In the library" : "Recently added"}
      </h2>
      <ul className="song-list">
        {shown.map((song) => (
          <li key={song.id}>
            <Link to={`/song/${song.slug}`} className="song-row">
              <Art src={song.artwork} alt="" />
              <span className="song-row-main">
                <span className="song-row-title">{titleOf(song)}</span>
                <span className="muted small">
                  {[artistOf(song), song.music_key, song.bpm ? `${Math.round(song.bpm)} bpm` : null]
                    .filter(Boolean).join(" · ")}
                </span>
              </span>
              <span className={isReady(song) ? "tag tag-ready" : "tag tag-working"}>
                {isReady(song) ? "Ready" : "Working"}
              </span>
            </Link>
          </li>
        ))}
        {!shown.length && (
          <li className="muted">
            {searching
              ? "Not in the library yet — pick one below and we will work it out."
              : "Nothing here yet — search for a song."}
          </li>
        )}
      </ul>

      {searching && missing.length > 0 && (
        <>
          <h2 className="section-heading serif">Not here yet</h2>
          <ul className="song-list">
            {missing.map((track) => (
              <li key={track.catalogId}>
                <div className="song-row">
                  <Art src={track.artwork} alt="" />
                  <span className="song-row-main">
                    <span className="song-row-title">{track.title}</span>
                    <span className="muted small">
                      {[track.artist, track.year, minutes(track.duration)]
                        .filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  {signedIn ? (
                    <button
                      className="btn"
                      onClick={() => add(track)}
                      disabled={busy === track.catalogId}
                    >
                      {busy === track.catalogId ? "Adding…" : "Work it out"}
                    </button>
                  ) : (
                    <Link className="btn" to="/signin">Sign in to add</Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <p className="muted small">
            The first time takes about a minute. After that the song is instant,
            for everyone.
          </p>
        </>
      )}
    </div>
  );
}
